<template>
  <DashboardLayout>
    <Breadcrumb :model="items" />

    <!-- Event Details Popover -->
    <EventPopover
      :visible="showEventPopover"
      :event="selectedEvent"
      :isLocked="isEventLocked"
      :eventElement="eventElement"
      @close="closeEventPopover"
    />

    <!-- Unconfirmed Completed Classes Popup -->
    <Dialog
      v-model:visible="showCompletedClassesDialog"
      modal
      header="Classes Awaiting Your Confirmation"
      :style="{ width: '35vw', maxWidth: '500px' }"
      :breakpoints="{ '1200px': '45vw', '960px': '60vw', '641px': '95vw' }"
    >
      <div class="p-fluid pr-1" style="max-height: 60vh; overflow-y: auto;">
        <p class="mb-4 text-sm text-gray-600">
          The following sessions have ended. Please confirm them to finalize records.
        </p>

        <div v-for="session in unconfirmedCompletedClasses" :key="session._id"
          class="session-card border-1 border-round surface-border p-3 mb-3 shadow-1">

          <div class="flex justify-content-between align-items-start mb-2">
            <div class="flex align-items-center gap-2">
              <!-- <Avatar :image="session.tutorId?.profilePhotoURL" shape="circle" size="normal" /> -->
              <div>
                <div class="font-bold text-base line-height-2">{{ cleanupLables(session.classId?.subject) }}</div>
                <div class="text-xs text-gray-500">{{ session.tutorId?.username }}</div>
                <Tag v-if="session.classConfirmedByTutor" value="Tutor Confirmed" severity="success" style="font-size: 0.65rem; margin-top: 3px;" />
              </div>
            </div>
            <!-- <Tag :value="session.classId?.classModel?.replace(/_/g, ' ')" severity="info" style="font-size: 0.7rem" /> -->
          </div>

          <div class="surface-100 p-2 border-round text-xs mb-3">
            <div class="flex align-items-center mb-1">
              <i class="pi pi-calendar mr-2" style="color: #dc2626;"></i>
              <span>{{ formatDate(session.date) }}</span>
            </div>
            <div class="flex align-items-center">
              <i class="pi pi-clock mr-2" style="color: #dc2626;"></i>
              <span>{{ formatTime(session.start) }} - {{ formatTime(session.end) }}</span>
            </div>
          </div>

          <Button
            label="Confirm Class Completed"
            icon="pi pi-check-circle"
            class="red-button-small p-button-sm w-full"
            :loading="sessionConfirming[session._id]"
            @click="confirmSession(session)"
          />
        </div>
      </div>

      <template #footer>
        <Button label="Close" icon="pi pi-times" @click="showCompletedClassesDialog = false" class="p-button-text p-button-sm" style="color: #dc2626;" />
      </template>
    </Dialog>

    <!-- Student Payment Dialog -->
    <Dialog
      v-model:visible="showStudentPaymentDialog"
      modal
      header="Payment Required"
      :style="{ width: '35vw', maxWidth: '500px' }"
      :breakpoints="{ '1200px': '45vw', '960px': '60vw', '641px': '95vw' }"
    >
      <div class="p-fluid">
        <p class="mb-4 text-sm text-gray-600">
          Payment is required before you can confirm this class as completed. Please complete the payment first.
        </p>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times" @click="showStudentPaymentDialog = false" class="p-button-text p-button-sm p-button-secondary" :disabled="isProcessingStudentPayment" />
        <Button label="Pay Now" icon="pi pi-credit-card" class="red-button-small p-button-sm" @click="payForSession" :loading="isProcessingStudentPayment" autofocus />
      </template>
    </Dialog>

    <!-- No Saved Card Dialog -->
    <Dialog
      v-model:visible="showNoCardDialog"
      modal
      header="Add a Payment Card"
      :style="{ width: '35vw', maxWidth: '500px' }"
      :breakpoints="{ '1200px': '45vw', '960px': '60vw', '641px': '95vw' }"
    >
      <div class="p-fluid">
        <div class="flex align-items-start gap-3">
          <i class="pi pi-credit-card mt-1" style="color: #dc2626; font-size: 1.25rem;"></i>
          <p class="m-0 text-sm text-gray-600">
            You don't have a saved payment card yet, so this session charge can't be processed.
            Please go to <b>Settings &rarr; Payment</b> and add a card before the session charges.
          </p>
        </div>
      </div>
      <template #footer>
        <Button label="Not Now" icon="pi pi-times" @click="showNoCardDialog = false" class="p-button-text p-button-sm p-button-secondary" />
        <Button label="Go to Settings" icon="pi pi-credit-card" class="red-button-small p-button-sm" @click="goToAddCard" autofocus />
      </template>
    </Dialog>

    <InviteStudentDialog v-model:visible="showInviteStudentDialog" />

    <div class="px-4 calendar-container">
      <div class="flex justify-content-end mb-3">
        <Button label="Invite a friend" icon="pi pi-user-plus" style="background: linear-gradient(360deg, #FFB8C5 -35.88%, #A72E44 100%)" @click="showInviteStudentDialog = true" />
      </div>
      <FullCalendar :options="calendarOptions" />

      <!-- Disclaimer -->
      <div class="text-center w-full max-w-4xl py-4">
        <h3 class="disclaimer-heading text-lg font-semibold mb-2" style="color: #dc2626;">Disclaimer</h3>
        <p class="text-700 text-sm m-0 mb-2">
          To access Al features, only platform-approved Al tools may be used.
        </p>
        <p class="text-700 text-sm m-0">
          Unauthorized tools are strictly prohibited.
        </p>
      </div>

    </div>
  </DashboardLayout>
</template>

<script setup>
import Breadcrumb from 'primevue/breadcrumb';
import FullCalendar from '@fullcalendar/vue3'
import timeGridPlugin from '@fullcalendar/timegrid'
import DashboardLayout from "@/layout/DashboardLayout.vue";
import { getSchedule, getUnconfirmedCompletedClasses, confirmCompletedClass } from "@/api/student";
import payments from "@/api/payments";
import { onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import EventPopover, { useEventPopover } from "@/components/calendarEventpopover.vue";
import InviteStudentDialog from "@/components/InviteStudentDialog.vue";
import { cleanupLables } from "@/utils/cleanups";
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
// import Avatar from 'primevue/avatar';
import Tag from 'primevue/tag';
import { notifyError, notifySuccess } from "@/utils/notification";

const items = [
  { label: 'Dashboard' },
  { label: 'Class Schedules' },
]

const {
  showEventPopover,
  selectedEvent,
  isEventLocked,
  eventElement,
  handleEventClick,
  handleEventMouseEnter,
  handleEventMouseLeave,
  closeEventPopover,
  cleanup
} = useEventPopover();

// --- Completed Classes Confirmation ---
const showCompletedClassesDialog = ref(false);
const unconfirmedCompletedClasses = ref([]);
const sessionConfirming = ref({});
const showStudentPaymentDialog = ref(false);
const showNoCardDialog = ref(false);
const selectedUnpaidSession = ref(null);
const isProcessingStudentPayment = ref(false);
const showInviteStudentDialog = ref(false);

const router = useRouter();

// True only when the student has a saved pre-approved card that can be auto-charged.
async function hasPreapprovedCard() {
  try {
    const res = await payments.getMyToken();
    return res?.data?.data?.hasToken === true;
  } catch {
    return false;
  }
}

function goToAddCard() {
  showNoCardDialog.value = false;
  router.push({ path: '/studentSettings', query: { tab: 'payment' } });
}

async function fetchUnconfirmedCompletedClasses() {
  try {
    const res = await getUnconfirmedCompletedClasses();
    unconfirmedCompletedClasses.value = res.data.data ?? res.data;
    if (unconfirmedCompletedClasses.value.length > 0) {
      showCompletedClassesDialog.value = true;
    }
  } catch (error) {
    console.error("Error fetching unconfirmed completed classes:", error);
  }
}

function removeConfirmedSession(id) {
  unconfirmedCompletedClasses.value = unconfirmedCompletedClasses.value.filter(s => s._id !== id);
  if (unconfirmedCompletedClasses.value.length === 0) showCompletedClassesDialog.value = false;
}

async function confirmSession(session) {
  sessionConfirming.value[session._id] = true;
  try {
    await confirmCompletedClass(session._id);
    removeConfirmedSession(session._id);
    notifySuccess("Confirmed", "Class marked as completed.");
  } catch (e) {
    const requiresPayment = e.response?.status === 400 &&
      (e.response?.data?.data?.requiresPayment || e.response?.data?.requiresPayment);
    if (requiresPayment) {
      selectedUnpaidSession.value = session;
      // No saved card means there is nothing to auto-charge — guide the student to
      // add a card in settings before the session charge can go through.
      if (!(await hasPreapprovedCard())) {
        showNoCardDialog.value = true;
        return;
      }
      // Auto-pay with saved card first, only show dialog if that fails too
      try {
        await payments.payForClass(session._id);
        await confirmCompletedClass(session._id);
        removeConfirmedSession(session._id);
        notifySuccess("Confirmed", "Payment processed and class confirmed.");
      } catch {
        showStudentPaymentDialog.value = true;
      }
    } else {
      notifyError("Confirmation Failed", e.response?.data?.message || "Please try again later.");
    }
  } finally {
    sessionConfirming.value[session._id] = false;
  }
}

async function payForSession() {
  if (!selectedUnpaidSession.value) return;
  isProcessingStudentPayment.value = true;
  try {
    // Guard against the card being removed between dialogs — redirect to add a card.
    if (!(await hasPreapprovedCard())) {
      showStudentPaymentDialog.value = false;
      showNoCardDialog.value = true;
      return;
    }
    await payments.payForClass(selectedUnpaidSession.value._id);
    showStudentPaymentDialog.value = false;
    notifySuccess("Payment Successful", "You can now confirm the class.");
    await confirmSession({ ...selectedUnpaidSession.value });
  } catch (error) {
    notifyError("Payment Failed", "Payment could not be processed. Please try again.");
  } finally {
    isProcessingStudentPayment.value = false;
  }
}

const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
const formatTime = (timeString) => new Date(timeString).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });

// --- End Completed Classes Confirmation ---

const getDynamicScrollTime = () => {
  const now = new Date();
  let hour = now.getHours();
  let targetHour = Math.max(0, hour - 1);
  return targetHour.toString().padStart(2, '0') + ':00:00';
};

const getDynamicMaxTime = () => {
  const now = new Date();
  let hour = now.getHours();
  let targetHour = Math.max(0, hour - 1);
  return (targetHour + 24).toString() + ':00:00';
};

const calendarOptions = ref({
  plugins: [timeGridPlugin],
 initialView: window.innerWidth < 1024 ? 'timeGridDay' : 'timeGridWeek',
  headerToolbar: {
    left: 'title',
    center: '',
    right: 'prev,today,next',
  },
  titleFormat: {
    year: 'numeric',
    month: 'long'
  },
   height: 'auto',
  contentHeight: 'auto',
  aspectRatio: 1.5,
  handleWindowResize: true,
  windowResizeDelay: 100,
  windowResize: (arg) => {
    const calendar = arg.view.calendar;
    if (window.innerWidth < 1024 && arg.view.type !== 'timeGridDay') {
      calendar.changeView('timeGridDay');
    } else if (window.innerWidth >= 1024 && arg.view.type !== 'timeGridWeek') {
      calendar.changeView('timeGridWeek');
    }
  },
  expandRows: true,
  slotMinTime: getDynamicScrollTime(),
  slotMaxTime: getDynamicMaxTime(),
  nowIndicator: true,
  allDaySlot: false,
  // slotDuration: '01:00:00',
  dayHeaderFormat: { day: 'numeric', weekday: 'short' },
  events: [],
  // Add event interaction handlers
  eventClick: handleEventClick,
  eventMouseEnter: handleEventMouseEnter,
  eventMouseLeave: handleEventMouseLeave,
  eventContent: function (arg) {
    // Access the custom data via arg.event.extendedProps
    const subject = cleanupLables(arg.event.title);
    const classType = arg.event.extendedProps.classType;
    const location = arg.event.extendedProps.location;
    const dispStart = new Date(arg.event.extendedProps.displayStart);
    const dispEnd = new Date(arg.event.extendedProps.displayEnd);
    const time = dispStart.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) + ' - ' + dispEnd.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const withWho = arg.event.extendedProps.with;
    const link = arg.event.extendedProps.classLink;
    // Create the custom HTML element
    let customHtml = document.createElement('div');
    customHtml.innerHTML = `
      <div class="fc-event-main-frame" style="text-align: center; padding-top: 20px">
        <div class="fc-event-title" style=font-weight:900>${subject}</div>
        <div class="event-description-snippet">${classType}</div>
        <div class="event-description-snippet">${location}</div>
        <div class="event-description-snippet">${time}</div>
        <div class="event-description-snippet" style="text-align: center"><b>${withWho}</b></div>
        ${link ? `<div class="event-description-snippet"><a href="${link}" target="_blank"><b>Meet Link</b></a></div>` : ''}
      </div>  
    `;

    return { domNodes: [customHtml] }
  }
})

onMounted(() => {
  loadSchedules();
  fetchUnconfirmedCompletedClasses();
})

onUnmounted(() => {
  cleanup();
});

async function loadSchedules() {
  try {
    const response = await getSchedule();
    const classes = response.data.data;
    const events = await processScheduleData(classes);
    calendarOptions.value.events = events;
  } catch (error) {
    console.error("Error loading schedules:", error);
  }
}

const processScheduleData = async (rawData) => {
  const COLORS = {
    PASSED: { backgroundColor: '#EF9A9A', borderColor: '#E57373', textColor: '#C62828' },
    NEXT:   { backgroundColor: '#A5D6A7', borderColor: '#81C784', textColor: '#388E3C' },
    FUTURE: { backgroundColor: '#90CAF9', borderColor: '#64B5F6', textColor: '#1565C0' }
  };

  const now = new Date();

  // Convert UTC to IST floating datetime (no Z). IST = UTC+5:30 = +330 min.
  const toISTISO = (utcString) => {
    const date = new Date(utcString);
    const ist = new Date(date.getTime() + 330 * 60000);
    return ist.toISOString().slice(0, 19);
  };

  // When slotMaxTime > "24:00:00", FullCalendar places events whose IST time
  // falls before slotMinTime into the *previous* day's overflow column.
  // Fix: shift such events +1 calendar day so FullCalendar puts them in the
  // column whose header matches the correct IST date.
  const slotMinMinutes = Math.max(0, new Date().getHours() - 1) * 60;

  const shiftDateByOne = (istISO) => {
    const [datePart, time] = istISO.split('T');
    const [y, mo, d] = datePart.split('-').map(Number);
    const shifted = new Date(Date.UTC(y, mo - 1, d + 1));
    return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(shifted.getUTCDate()).padStart(2, '0')}T${time}`;
  };

  const fixOverflow = (startIST, endIST) => {
    const [sh, sm] = startIST.split('T')[1].split(':').map(Number);
    const [eh, em] = endIST.split('T')[1].split(':').map(Number);
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;

    if (startMins < slotMinMinutes) {
      if (endMins > slotMinMinutes) {
        // Event spans slotMinTime boundary — clamp start to slotMinTime on same
        // date so the event stays in the correct day column without wrapping.
        const datePart = startIST.split('T')[0];
        const clampedH = String(Math.floor(slotMinMinutes / 60)).padStart(2, '0');
        const clampedM = String(slotMinMinutes % 60).padStart(2, '0');
        return { start: `${datePart}T${clampedH}:${clampedM}:00`, end: endIST };
      }
      // Entire event is before slotMinTime — shift +1 day into correct column.
      return { start: shiftDateByOne(startIST), end: shiftDateByOne(endIST) };
    }
    return { start: startIST, end: endIST };
  };

  const sortedClasses = rawData
    .map(item => ({
      ...item,
      startDate: new Date(item.start),
      endDate: new Date(item.end)
    }))
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

  const futureClasses = sortedClasses.filter(item => item.startDate.getTime() > now.getTime());
  const nextClassStart = futureClasses.length > 0 ? futureClasses[0].startDate.getTime() : null;

  const processedEvents = sortedClasses.map(classItem => {
    const classStartTimestamp = classItem.startDate.getTime();
    let colorProfile = COLORS.FUTURE;

    if (classStartTimestamp < now.getTime()) {
      colorProfile = COLORS.PASSED;
    } else if (classStartTimestamp === nextClassStart) {
      colorProfile = COLORS.NEXT;
    }

    const subjectTitle = classItem.subject
      ? classItem.subject.charAt(0).toUpperCase() + classItem.subject.slice(1)
      : 'Unknown';

    const startIST = toISTISO(classItem.start);
    const endIST = toISTISO(classItem.end);
    const { start, end } = fixOverflow(startIST, endIST);

    return {
      title: subjectTitle,
      start,
      end,
      extendedProps: {
        classType: classItem.classType,
        classModel: classItem.classModel,
        location: classItem.classLocation || '',
        with: classItem.student || '',
        tutor: classItem.tutor || 'N/A',
        note: classItem.note || '',
        groupClass: classItem.groupClass || false,
        otherStudents: classItem.otherStudents,
        classLink: classItem.classLink || '',
        displayStart: startIST,
        displayEnd: endIST,
      },
      ...colorProfile
    };
  });

  return processedEvents;
};
</script>


<style>
.calendar-container {
  width: 100%;
  height: auto;
}
.fc-timegrid-slot {
    height: 120px !important;
  }
.fc-col-header-cell-cushion {
  font-weight: 400;
  text-align: center;
  padding: 6px 0;
  font-size: 14px;
  font-family: Poppins;
}

.fc-toolbar-title {
  font-weight: 400;
  font-size: 17px !important;
  font-family: Poppins;
}

.fc-button-primary {
  background-color: transparent !important;
  border-color: transparent !important;
  color: var(--gray-600) !important;
  font-size: 17px !important;
}

.fc-timeGridWeek-view .fc-col-header-cell.fc-day-today {
  background-color: var(--orange-50) !important;
}

.fc-timeGridWeek-view .fc-timegrid-col.fc-day-today {
  background-color: var(--orange-50) !important;
}
.fc-timeGridDay-view .fc-col-header-cell.fc-day-today {
  background-color: var(--orange-50) !important;
}

.fc-timeGridDay-view .fc-timegrid-col.fc-day-today {
  background-color: var(--orange-50) !important;
}
@media (max-width: 768px) {
  .fc-timegrid-slot {
    height: 100px !important;
  }
}
</style>
